'use client';

import { useState } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import Link from 'next/link';
import { useCartStore } from '@/store';
import { useCurrency } from '@/hooks/useCurrency';
import styles from './CartDrawer.module.css';

function CartItemImage({ src, name }: { src: string; name: string }) {
    const [error, setError] = useState(false);
    if (src && src !== '/images/product-placeholder.jpg' && !error) {
        return (
            <Image
                src={src}
                alt={name}
                fill
                sizes="60px"
                style={{ objectFit: 'cover', borderRadius: 'var(--radius-md)' }}
                onError={() => setError(true)}
            />
        );
    }
    return (
        <svg viewBox="0 0 60 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="10" y="20" width="40" height="50" rx="5" fill="#C9A45C" />
            <rect x="12" y="5" width="36" height="20" rx="3" fill="#D4AF37" />
        </svg>
    );
}

export default function CartDrawer() {
    const {
        items,
        isOpen,
        closeCart,
        removeItem,
        updateQuantity,
        getSubtotal,
        getTotalItems,
    } = useCartStore();

    const { format } = useCurrency();
    const subtotal = getSubtotal();
    const totalItems = getTotalItems();

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        className={styles.backdrop}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeCart}
                    />

                    {/* Drawer */}
                    <motion.div
                        className={styles.drawer}
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    >
                        {/* Header */}
                        <div className={styles.header}>
                            <div className={styles.headerTitle}>
                                <ShoppingBag size={20} />
                                <h2>Your Cart</h2>
                                <span className={styles.itemCount}>({totalItems})</span>
                            </div>
                            <button className={styles.closeButton} onClick={closeCart}>
                                <X size={24} />
                            </button>
                        </div>

                        {/* Content */}
                        <div className={styles.content}>
                            {items.length === 0 ? (
                                <div className={styles.empty}>
                                    <ShoppingBag size={48} />
                                    <p>Your cart is empty</p>
                                    <button className="btn btn-secondary" onClick={closeCart}>
                                        Continue Shopping
                                    </button>
                                </div>
                            ) : (
                                <ul className={styles.items}>
                                    {items.map((item) => (
                                        <motion.li
                                            key={item.product.id}
                                            className={styles.item}
                                            layout
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                        >
                                            {/* Product Image */}
                                            <div className={styles.itemImage} style={{ position: 'relative' }}>
                                                <CartItemImage src={item.product.imageUrl} name={item.product.name} />
                                            </div>

                                            {/* Item Details */}
                                            <div className={styles.itemDetails}>
                                                <h4 className={styles.itemName}>{item.product.name}</h4>
                                                <p className={styles.itemPrice}>
                                                    {format(item.product.price)}
                                                </p>

                                                {/* Quantity Controls */}
                                                <div className={styles.quantityControls}>
                                                    <button
                                                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                                                        disabled={item.quantity <= 1}
                                                    >
                                                        <Minus size={14} />
                                                    </button>
                                                    <span>{item.quantity}</span>
                                                    <button
                                                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                                                    >
                                                        <Plus size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Item Total & Remove */}
                                            <div className={styles.itemActions}>
                                                <span className={styles.itemTotal}>
                                                    {format(item.product.price * item.quantity)}
                                                </span>
                                                <button
                                                    className={styles.removeButton}
                                                    onClick={() => removeItem(item.product.id)}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </motion.li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Footer */}
                        {items.length > 0 && (
                            <div className={styles.footer}>
                                <div className={styles.subtotal}>
                                    <span>Subtotal</span>
                                    <span className={styles.subtotalAmount}>
                                        {format(subtotal)}
                                    </span>
                                </div>
                                <p className={styles.shippingNote}>
                                    Shipping and taxes calculated at checkout
                                </p>
                                <Link href="/checkout" onClick={closeCart}>
                                    <button className="btn btn-primary btn-lg" style={{ width: '100%' }}>
                                        Checkout
                                    </button>
                                </Link>
                                <button
                                    className="btn btn-ghost"
                                    style={{ width: '100%', marginTop: 'var(--space-2)' }}
                                    onClick={closeCart}
                                >
                                    Continue Shopping
                                </button>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
